import { PageSizing } from 'components/Layout/PageContentWidth';
import { ContentCard, PageWrapper } from '../styles';
import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { Spacer } from '@kizen/kds/Spacer';
import { useQuery } from '@tanstack/react-query';
import { getPaymentMethodsQuery } from 'query/billing';
import { useCallback, useMemo, useRef } from 'react';
import { Button } from '@kizen/kds/Button';
import { AddCardModal } from './components/AddCardModal';
import useModal from '__components/Modals/useModal';
import Loader from '__components/Kizen/Loader';
import Icon from '__components/Kizen/Icon';
import IconButtonMenu from '__components/Kizen/IconButtonMenu';
import { colorsButton } from 'app/colors';
import { toastVariant, useToast } from '__components/ToastProvider';
import { useHistory } from 'react-router-dom';
import BillingService from 'services/BillingService';
import { useBillingPermissions } from 'ts-components/hooks/permissions/billing';
import { useDispatch } from 'react-redux';
import { refreshBootstrapBusiness } from 'store/authentication/authenticationAction';

const brandColors: Record<string, string> = {
  visa: '#1A1F71',
  mastercard: '#F79E1B',
  amex: '#016FD0',
  discover: '#EA4209',
};

export const BillingDetailsPage = () => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const history = useHistory();
  const dispatch = useDispatch();

  const qs = new URLSearchParams(window.location.search);
  const isSuccess = qs.get('success') === 'true';

  const {
    data: paymentMethodsData,
    isLoading: paymentMethodsLoading,
    refetch: refetchPaymentMethods,
  } = useQuery(
    getPaymentMethodsQuery({
      throwOnError: false,
      // This query will fail if the user hasn't set up any payment methods, because
      // there is no Stripe customer created yet. It gets created automatically so this
      // is fine, but should be improved. (keegandonley)
      retry: (failureCount, error: any) => {
        if (error?.status === 400) {
          return false;
        }

        return failureCount < 3;
      },
    })
  );

  const defaultPaymentMethod = paymentMethodsData?.default_payment_method_id;

  const paymentMethods = useMemo(() => {
    return paymentMethodsData?.payment_methods || [];
  }, [paymentMethodsData]);

  const [addCardModalProps, , addCardModal] = useModal({});

  const deletePaymentMethod = useCallback(
    async (paymentMethod: string) => {
      try {
        const res = await BillingService.deletePaymentMethod(paymentMethod);

        if (res.data.message) {
          showToast({
            message: res.data.message,
            variant:
              res.status === 200 ? toastVariant.SUCCESS : toastVariant.FAILURE,
          });
        }

        refetchPaymentMethods();
      } catch (ex: any) {
        if (typeof ex?.response?.data === 'string') {
          showToast({
            message: ex?.response?.data,
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [showToast, refetchPaymentMethods]
  );

  const setDefault = useCallback(
    async (paymentMethod: string, notify = true) => {
      const res = await BillingService.setDefaultPaymentMethod(paymentMethod);

      if (res.status === 200) {
        if (notify) {
          showToast({
            message: t('Successfully updated default payment method'),
            variant: toastVariant.SUCCESS,
          });
        }
      } else {
        showToast({
          message: t('Failed to update default payment method'),
          variant: toastVariant.FAILURE,
        });
      }

      refetchPaymentMethods();
      dispatch(refreshBootstrapBusiness());
    },
    [showToast, refetchPaymentMethods, t, dispatch]
  );

  const getMenuHandler = useCallback(
    (paymentMethod: string) => {
      return ({ value }: { value: string }) => {
        switch (value) {
          case 'delete':
            return deletePaymentMethod(paymentMethod);
          case 'make_default':
            return setDefault(paymentMethod);
        }
      };
    },
    [deletePaymentMethod, setDefault]
  );

  if (isSuccess) {
    showToast({
      message: t('Successfully added payment method'),
      variant: toastVariant.SUCCESS,
    });
    history.replace('/settings/billing/details');
  }

  const hasRunCheckForMethod = useRef(false);
  if (
    paymentMethods.length &&
    !defaultPaymentMethod &&
    !hasRunCheckForMethod.current
  ) {
    hasRunCheckForMethod.current = true;
    setDefault(paymentMethods[0].id, false);
  }

  const billingPermissions = useBillingPermissions();

  const isReadOnly =
    billingPermissions.canViewPaymentMethods &&
    !billingPermissions.canEditPaymentMethods;

  if (paymentMethodsLoading) {
    return <Loader loading />;
  }

  return (
    <>
      <PageSizing>
        <div className="pb-20">
          <PageWrapper>
            <div className="grid grid-cols-2 gap-20">
              <ContentCard>
                <Typography variant="header" size="2xl" weight="semibold">
                  {t('Payment Methods')}
                </Typography>
                <Spacer size={20} />
                <div className="flex flex-col gap-15">
                  {paymentMethods.map((paymentMethod) => (
                    <div
                      key={paymentMethod.id}
                      className="flex items-center gap-10"
                    >
                      <Icon
                        color={brandColors[paymentMethod.card.brand]}
                        icon={`cc-${paymentMethod.card.brand}`}
                      />
                      <Typography>
                        {paymentMethod.card.brand} {t('ending in')}{' '}
                        {paymentMethod.card.last4}
                      </Typography>
                      {paymentMethod.id === defaultPaymentMethod ? (
                        <Typography
                          className="border-1 rounded-full p-2 opacity-75 text-teal-500"
                          size="xs"
                        >
                          {t('default')}
                        </Typography>
                      ) : null}
                      <Typography className="ml-auto">
                        {t('Valid until')} {paymentMethod.card.exp_month}/
                        {paymentMethod.card.exp_year}
                      </Typography>
                      {isReadOnly ? null : (
                        <IconButtonMenu
                          overlay
                          overlayOffsetY={10}
                          position="right"
                          sizing="dense"
                          color={colorsButton.iconGray}
                          options={[
                            {
                              value: 'delete',
                              label: t('Remove'),
                            },
                            paymentMethod.id !== defaultPaymentMethod
                              ? {
                                  value: 'make_default',
                                  label: t('Make Default'),
                                }
                              : undefined,
                          ].filter(Boolean)}
                          onChange={getMenuHandler(paymentMethod.id)}
                        >
                          <Icon icon="three-dot" />
                        </IconButtonMenu>
                      )}
                    </div>
                  ))}
                </div>
                {paymentMethods.length ? <Spacer size={20} /> : null}
                {isReadOnly ? null : (
                  <div className="text-right">
                    <Button onClick={addCardModal.show} maxWidth={false}>
                      {t('Add New Payment Method')}
                    </Button>
                  </div>
                )}
              </ContentCard>
            </div>
          </PageWrapper>
        </div>
      </PageSizing>
      {addCardModalProps.show ? <AddCardModal {...addCardModalProps} /> : null}
    </>
  );
};
